import { useState } from "react";
import { MapPin, Users, Layers, Ruler, Truck, MousePointer2 } from "lucide-react";
import { num } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SectionShell } from "./SectionShell";
import { ProposalRigFigure } from "./ProposalRigFigure";
import { ProposalSiteMap } from "./ProposalSiteMap";
import { paragraphs } from "./proposalUtils";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/70"><Icon className="h-4 w-4" /></div>
    <div>
      <div className="text-[12.5px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[15px] font-medium">{value}</div>
    </div>
  </div>
);

/** Interactive rig explorer: floor toggle + legend hover highlights the module type in the figure. */
const RigExplorer = ({ sol }) => {
  const [floor, setFloor] = useState(null);
  const [hl, setHl] = useState(null);
  const floors = sol.preview?.floors || sol.kpi?.floors || 1;
  const kinds = [...new Set(sol.bom.map((r) => r.kind))];
  return (
    <div className="mt-14" data-testid="proposal-rig-explorer">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="proposal-display text-[22px] font-semibold">Utforsk riggen</h3>
          <p className="mt-1 text-[13.5px] text-muted-foreground">{sol.source === "rig_design" ? `Riggdesign${sol.design_version ? ` v${sol.design_version}` : ""} – slik riggen faktisk er tegnet for tomten.` : "Illustrasjon av modulsammensetningen – antall og størrelser fra prisspesifikasjonen, oppstilling er veiledende."}</p>
        </div>
        {floors > 1 && (
          <div className="inline-flex h-9 items-center rounded-full bg-muted p-1 text-[13px]" role="tablist" aria-label="Etasje" data-testid="proposal-floor-toggle">
            {[null, ...Array.from({ length: floors }, (_, i) => i)].map((f) => (
              <button
                key={String(f)}
                type="button"
                role="tab"
                aria-selected={floor === f}
                onClick={() => setFloor(f)}
                className={cn("h-7 rounded-full px-3 font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60", floor === f ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}
                data-testid={`proposal-floor-${f == null ? "all" : f}`}
              >
                {f == null ? "Alle etasjer" : `${f + 1}. etasje`}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        <div className="relative overflow-hidden rounded-3xl bg-[hsl(var(--navy))] p-6 sm:p-8 ring-1 ring-black/5">
          <div className="absolute inset-0 module-grid-bg opacity-70" aria-hidden="true" />
          <ProposalRigFigure preview={sol.preview} height={340} floor={floor} highlight={hl} animate={false} className="relative" testId="proposal-explorer-figure" />
          <div className="relative mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[12px] text-white/50">
            <span className="hidden items-center gap-1.5 sm:inline-flex"><MousePointer2 className="h-3.5 w-3.5" /> Hold over en modultype for å se hvor den sitter</span>
            <span className="font-mono tabular whitespace-nowrap">{num(sol.kpi.modules)} moduler · {kinds.length} typer</span>
          </div>
        </div>
        <ul className="divide-y divide-border/70 self-start" data-testid="proposal-composition" onMouseLeave={() => setHl(null)}>
          {sol.bom.map((r) => (
            <li
              key={r.module_type_id}
              className={cn("-mx-3 flex cursor-default items-center gap-4 rounded-xl px-3 py-3 transition-colors duration-150", hl === r.module_type_id && "bg-muted")}
              onMouseEnter={() => setHl(r.module_type_id)}
              onFocus={() => setHl(r.module_type_id)}
              onBlur={() => setHl(null)}
              tabIndex={0}
              data-testid={`proposal-bom-${r.code || r.module_type_id}`}
            >
              <span className="h-9 w-9 shrink-0 rounded-lg ring-1 ring-black/10" style={{ backgroundColor: r.color || "#d8d2c4" }} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-medium">{r.name}</div>
                <div className="text-[12.5px] text-muted-foreground">{r.kind_label}{r.code ? ` · ${r.code}` : ""}{r.area_m2 ? ` · ${num(r.area_m2)} m²` : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[15px] tabular">× {num(r.quantity)}</div>
                {r.beds ? <div className="text-[12px] text-muted-foreground">{num(r.beds)} senger</div> : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/** 01 · Løsningen: description prose, key facts, the interactive rig explorer and the delivery site on a real map. */
export const ProposalSolution = ({ data }) => {
  const q = data.quote;
  const p = data.project;
  const sol = data.solution;
  const paras = paragraphs(q.description);
  const kpi = sol?.kpi || {};

  return (
    <SectionShell id="losning" no="01" title="Løsningen" lede="Hva som leveres, hvor, og hvordan riggen er satt sammen.">
      {paras.length > 0 ? (
        <div className="proposal-prose max-w-[680px] text-[16.5px] leading-8 text-foreground/90" data-testid="proposal-description">
          {paras.map((t, i) => <p key={i} className={i === 0 ? "text-[18px] leading-8 sm:text-[19px] sm:leading-9" : ""}>{t}</p>)}
        </div>
      ) : (
        <p className="max-w-[680px] text-[16.5px] leading-8 text-muted-foreground" data-testid="proposal-description">Komplett modulløsning levert, montert og driftsklar i henhold til prisspesifikasjonen nedenfor.</p>
      )}

      {(p || kpi.modules) && (
        <div className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="proposal-solution-stats">
          {p?.address && <Stat icon={MapPin} label="Leveringssted" value={p.address} testId="proposal-stat-address" />}
          {(kpi.beds || p?.capacity_persons) && <Stat icon={Users} label="Kapasitet" value={kpi.beds ? `${num(kpi.beds)} sengeplasser${p?.capacity_persons ? ` for ${num(p.capacity_persons)} personer` : ""}` : `${num(p.capacity_persons)} personer`} testId="proposal-stat-capacity" />}
          {kpi.modules && <Stat icon={Layers} label="Oppbygging" value={`${num(kpi.modules)} moduler${kpi.floors ? ` over ${kpi.floors} ${kpi.floors === 1 ? "etasje" : "etasjer"}` : ""}`} testId="proposal-stat-modules" />}
          {kpi.area_m2 && <Stat icon={Ruler} label="Bruttoareal" value={`ca. ${num(kpi.area_m2)} m²`} testId="proposal-stat-area" />}
          {kpi.transports && <Stat icon={Truck} label="Transport" value={`ca. ${num(kpi.transports)} transporter · ${kpi.delivery_days} ${kpi.delivery_days === 1 ? "dag" : "dager"} levering`} testId="proposal-stat-transport" />}
        </div>
      )}

      {sol?.bom?.length > 0 && sol.preview?.boxes?.length > 0 && <RigExplorer sol={sol} />}

      {data.logistics?.site && (
        <div className="mt-14" data-testid="proposal-site">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="proposal-display text-[22px] font-semibold">Leveringssted</h3>
              <p className="mt-1 text-[13.5px] text-muted-foreground">Planlagt levering fra vårt depot til tomten. Transport og montasje planlegges i detalj ved bestilling.</p>
            </div>
          </div>
          <ProposalSiteMap logistics={data.logistics} className="mt-6" height={340} />
        </div>
      )}
    </SectionShell>
  );
};
